import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import { 
    Area_Status_Enum,
    Area_Type_Enum,
    AreaFragment,
    Areas_Insert_Input,
    UpdateAreaMutationVariables
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    OperationsAdminVenueAreaCreateFragment, 
    OperationsAdminVenueAreaCreateSchema, 
    OperationsAdminVenueAreaUpdateFragment, 
    OperationsAdminVenueAreaUpdateSchema 
} from "src/components/operations/administrator/areas/OperationsAdminVenueAreaSchemas"
import { extractCurrentItemData } from "src/utils/forms"

type FormPayloadType = OperationsAdminVenueAreaCreateFragment

let initialState: FormPayloadType = {
    type: Area_Type_Enum.EventSpace,
    name: '',
    description: '',
    poc: '',
    website: '',
    instagram: '',
};

export const CreateOperationsAdminVenueAreaDrawer: React.FC<{onClose():void}> = ({ onClose }) => {

    const { createItem, updateItem, deleteItem } = useActions().areas;
    const { loading, errors } = useAppState().areas;
    const { tenant_id: currentTenantId } = useAppState(state => state.auth)    
    
    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Areas_Insert_Input) => {
        return createItem({ venue_id: currentTenantId, ...payload })
    }

    return (
        <>
            <FormDrawer<FormPayloadType, AreaFragment, string>
                autoOpen={false}
                formSchema={OperationsAdminVenueAreaCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateOperationsAdminVenueAreaAction = () => {
    return (
        <PageDrawerAction<AreaFragment>
            title='Add Area'
            action={CreateOperationsAdminVenueAreaDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: OperationsAdminVenueAreaUpdateFragment = {
    status: Area_Status_Enum.Active,
    type: Area_Type_Enum.Bar,   
    name: '',
    description: '',
    poc: '',
    website: '',
    instagram: '',
};

export const UpdateOperationsAdminVenueAreaDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {

    
    const { updateItem, deleteItem, deselectItem } = useActions().areas;
    const { currentItem, loading, errors } = useAppState().areas;

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: OperationsAdminVenueAreaUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<OperationsAdminVenueAreaUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateAreaMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<OperationsAdminVenueAreaUpdateFragment, AreaFragment, string>
                autoOpen={false}
                formSchema={OperationsAdminVenueAreaUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateOperationsAdminVenueAreaAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().areas;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.areas.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<AreaFragment>
            title='Edit Area'
            action={UpdateOperationsAdminVenueAreaDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditOperationsAdminVenueAreaAction = () => {
    return (
        <PageDrawerAction<AreaFragment>
            title='Edit Area'
            action={UpdateOperationsAdminVenueAreaDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}